import React from 'react'
import { Box, Button } from '@mui/material'
import { Close, Done, QuestionMark, SkipNext, SkipPrevious } from '@mui/icons-material'
import { GameType, levelDown, levelUp } from '../Store/GameSlice'
import { useAppDispatch, useAppSelector } from '../hooks'
import { ThemeContext } from '../Providers/ThemeContext'

interface CardsPlayControlsProps {
  interactionState: string
  gameType: GameType
  onCorrect: () => void
  onWrong: () => void
  onQuestionMark: () => void
  onOk: () => void
  onSaveEdit: () => void
  onCancelEdit: () => void
}

const CardsPlayControls: React.FC<CardsPlayControlsProps> = (
  {
    interactionState,
    gameType,
    onCorrect,
    onWrong,
    onQuestionMark,
    onOk,
    onSaveEdit,
    onCancelEdit,
  }) => {
  const { darkMode } = React.useContext(ThemeContext)

  const dispatch = useAppDispatch()

  const level = useAppSelector(state => state.game.currentGame?.state?.level || 0)
  const maxLevel = useAppSelector(state => (state.game.currentGame?.state?.levels || 1) - 1)
  const onLevelDown = () => {
    if (level === 0) return
    dispatch(levelDown())
  }
  const onLevelUp = () => {
    if (level >= maxLevel) return
    dispatch(levelUp())
  }

  const showLevelChangers = gameType === 'NumbersGame'
  return (
    <Box display="flex" justifyContent="center" alignItems="center" gap={2} sx={{ padding: 2 }}>
      {showLevelChangers && (
        <Button
          className="action small"
          style={{ border: 'none' }}
          onClick={onLevelDown}
          disabled={level === 0}
          color="secondary"
        >
          <SkipPrevious />
        </Button>
      )}
      {interactionState === 'evaluate.normal' && (
        <>
          <Button
            className="action"
            onClick={onCorrect}
            variant={darkMode ? 'outlined' : 'contained'}
            color="success"
            data-testid="correct"
          >
            <Done />
          </Button>
          <Button
            className="action"
            onClick={onWrong}
            variant={darkMode ? 'outlined' : 'contained'}
            color="error"
            data-testid="wrong"
          >
            <Close />
          </Button>
        </>
      )}

      {interactionState === 'showQuestion' && (
        <>
          <Button
            className="action"
            onClick={onCorrect}
            variant="contained"
            color="success"
            disabled
            data-testid="correct"
          >
            <Done />
          </Button>
          <Button
            className="action"
            onClick={onQuestionMark}
            color="secondary"
            data-testid="dontknow"
          >
            <QuestionMark />
          </Button>
        </>
      )}

      {interactionState === 'evaluate.wrong' && (
        <>
          <Button
            className="action"
            onClick={onCorrect}
            variant="contained"
            color="success"
            disabled
            data-testid="correct"
          >
            <Done />
          </Button>
          <Button className="action" color="secondary" onClick={onOk} data-testid="ok">
            OK
          </Button>
        </>
      )}

      {interactionState === 'edit' && (
        <>
          <Button className="action" onClick={onSaveEdit} data-testid="edit-save">
            OK
          </Button>
          <Button
            className="action"
            onClick={onCancelEdit}
            data-testid="edit-cancel"
          >
            Cancel
          </Button>
        </>
      )}

      {showLevelChangers && (
        <Button
          className="action small"
          style={{ border: 'none' }}
          onClick={onLevelUp}
          disabled={level >= maxLevel}
          color="secondary"
        >
          <SkipNext />
        </Button>
      )}
    </Box>
  )
}

export default CardsPlayControls
